import React from 'react';
import { Link } from 'react-router-dom';
import '../styles/Documentation.css';

const Documentation: React.FC = () => (
    <main className="dashboard documentation" lang="nl">
        <h1>Uitleg: bijwerken, instellen en controleren</h1>
        <p className="documentation-intro">
            De portal, de configurator en SystemReporter werken samen, maar hebben elk een eigen taak.
            Hieronder lees je wanneer je welk onderdeel gebruikt.
        </p>

        <section className="documentation-section" aria-labelledby="quick-guide-heading">
            <h2 id="quick-guide-heading">Welke actie heb ik nodig?</h2>
            <dl className="documentation-quick-guide">
                <div>
                    <dt>Tools bijwerken of nieuwe opties ophalen</dt>
                    <dd><strong>Install Latest Update</strong> bij het apparaat in de portal.</dd>
                </div>
                <div>
                    <dt>De inrichting of instellingen aanpassen</dt>
                    <dd>Open de <strong>configurator op de klant-pc</strong> en klik daar op <strong>Update</strong>.</dd>
                </div>
                <div>
                    <dt>Bekijken wat er bij de klant draait</dt>
                    <dd>Bekijk de apparaatdetails en <strong>Reports</strong> in de portal.</dd>
                </div>
                <div>
                    <dt>Onderzoeken waarom iets niet goed werkt</dt>
                    <dd>Open <strong>Diagnostics / logs</strong> bij het apparaat en kies <strong>Retrieve logs</strong>.</dd>
                </div>
            </dl>
        </section>

        <section className="documentation-section" aria-labelledby="installer-heading">
            <h2 id="installer-heading">1. Install Latest Update: de tools bijwerken</h2>
            <p>
                Open het apparaat via <Link to="/devices">Devices</Link> en klik op <strong>Install Latest Update</strong>.
                Hiermee vraag je de installatie van het nieuwste updatepakket op de klant-pc aan.
                Dat pakket bevat onder andere:
            </p>
            <ul>
                <li>De configurator.</li>
                <li>De boot- en startupscripts die taken rond het opstarten uitvoeren.</li>
                <li>Ondersteunende tools, zoals de Focusrite MIDI Bridge.</li>
                <li>Bestanden voor de inrichting, zoals de bureaubladachtergrond.</li>
            </ul>
            <p>
                De ContentAgent is de achtergrondservice op de klant-pc die met de portal communiceert
                en opdrachten ophaalt en uitvoert. Het apparaat moet aan staan en internet hebben om
                de updateopdracht op te halen. Een aangevraagde update is dus niet meteen een afgeronde update:
                controleer de opdrachtstatus in de portal.
            </p>
            <p>
                Een nieuwe optie in de configurator, zoals ondersteuning voor de Cambiare 305,
                haal je eerst met deze knop op. Daarna kies en pas je de gewenste instelling toe
                in de configurator.
            </p>
        </section>

        <section className="documentation-section" aria-labelledby="configurator-heading">
            <h2 id="configurator-heading">2. De configurator: het systeem instellen</h2>
            <p>
                Met de configurator bepaal je hoe het systeem voor de klant wordt ingericht.
                Denk aan het orgelmodel, het aantal displays, licentiegegevens en de aanwezigheid
                van een grote setzer.
            </p>
            <p>
                Ook het bijwerken van DoMidi-layouts, Sweelinq-instellingen en Hauptwerk-instellingen
                gebeurt via de configurator. Pas de gewenste keuzes aan en klik daar op <strong>Update</strong>
                {' '}om ze toe te passen.
            </p>
            <p>
                <strong>Voorbeeld:</strong> blijkt dat een klant toch een grote setzer heeft?
                Vink die optie aan in de configurator en klik op Update.
                Alleen Install Latest Update in de portal aanklikken vervangt deze stap niet.
            </p>
        </section>

        <section className="documentation-section" aria-labelledby="reporter-heading">
            <h2 id="reporter-heading">3. SystemReporter: de situatie rapporteren</h2>
            <p>
                SystemReporter verzamelt informatie over de configuratie, hardware en software
                en rapporteert die naar een database. De geplande taak start ongeveer twee minuten
                na het aanmelden bij Windows; bij automatisch aanmelden is dat kort na het opstarten.
                Voor het versturen is een internetverbinding nodig.
            </p>
            <p>
                De bootstrap controleert vooraf automatisch of er een nieuwe SystemReporter-versie
                beschikbaar is en haalt die zo nodig op. SystemReporter levert ook de ContentAgent
                mee en installeert die bij het uitvoeren. De agent wordt dus niet alleen via een
                installer-update bijgewerkt.
            </p>
            <p>
                In de portal zie je per apparaat de gerapporteerde informatie en de SystemReporter-versie.
                Het rapport is een <strong>momentopname, geen live weergave</strong>.
                Controleer daarom de rapportdatum: een wijziging in de configurator is niet direct
                zichtbaar in een eerder rapport. Met Reports bekijk je informatie; je wijzigt er
                geen instellingen mee.
            </p>
        </section>

        <section className="documentation-section" aria-labelledby="logging-heading">
            <h2 id="logging-heading">4. Logging: terugkijken wat er is gebeurd</h2>
            <p>
                Een logbestand is een technisch dagboek: software schrijft erin welke stappen zijn
                uitgevoerd, wanneer dat gebeurde en welke meldingen of fouten daarbij optraden.
                Een systeemrapport laat zien <strong>wat er op de pc draait</strong>; logs helpen
                onderzoeken <strong>waarom iets niet goed werkt</strong>, bijvoorbeeld bij een
                mislukte update of een programma dat niet start.
            </p>
            <h3>Logs ophalen via het dashboard</h3>
            <ol>
                <li>Open het juiste apparaat via Devices en klik op <strong>Diagnostics / logs</strong>. De logviewer opent in een nieuw tabblad.</li>
                <li>Klik op <strong>Retrieve logs</strong> om een nieuwe verzameling aan te vragen.</li>
                <li>Wacht tot de opdracht is afgerond en controleer de datum bij <strong>Snapshot collected</strong>.</li>
                <li>Kies links een bron en een logbestand om de inhoud te bekijken.</li>
                <li>Gebruik zo nodig <strong>Download snapshot</strong> om de verzameling als JSON-bestand te bewaren voor verder onderzoek.</li>
            </ol>
            <p>
                Hiervoor heb je een Entra-beheerdersaccount nodig. Google-accounts en accounts zonder
                beheerrechten zien deze functie niet. Retrieve logs wordt beschikbaar zodra
                SystemReporter <strong>1.0.73 of nieuwer</strong> op het apparaat is gerapporteerd.
                Deze versie bevat de benodigde ContentAgent (1.0.2 of nieuwer);
                de installerversie is hiervoor niet bepalend.
            </p>
            <h3>Wat gebeurt er na Retrieve logs?</h3>
            <p>
                De portal zet een opdracht klaar. De ContentAgent op de klant-pc controleert normaal
                elke 60 seconden op opdrachten, leest de ondersteunde lokale logbestanden en uploadt
                een momentopname. Hiervoor is geen TeamViewer-sessie nodig, maar de pc moet wel aan
                staan, internet hebben en een werkende agent hebben.
            </p>
            <ul>
                <li><strong>Waiting for device:</strong> de opdracht staat klaar, maar is nog niet opgehaald; bijvoorbeeld omdat de pc offline is.</li>
                <li><strong>Collecting logs:</strong> de agent verwerkt de opdracht; het dashboard wacht op de upload en bevestiging.</li>
                <li><strong>Completed:</strong> de verzamelopdracht is afgerond. Dit betekent niet dat alle software op de pc foutloos werkt.</li>
                <li><strong>Failed:</strong> het verzamelen is mislukt. Lees de getoonde foutmelding.</li>
            </ul>
            <p>
                Een eerdere momentopname kan zichtbaar blijven terwijl een nieuwe aanvraag loopt.
                Controleer daarom altijd de verzameldatum en de melding dat je eventueel nog de
                vorige snapshot bekijkt. <strong>Refresh status</strong> vernieuwt alleen de weergave;
                voor nieuwe loggegevens klik je opnieuw op Retrieve logs zodra de lopende opdracht klaar is.
            </p>
            <h3>Welke logs zijn beschikbaar?</h3>
            <p>
                De viewer verzamelt vaste logbestanden van de Startup Launcher, de installer,
                SystemReporter, de Amplifier en de bootstrap. Niet elk programma schrijft al een
                ondersteund logbestand: er is bijvoorbeeld nog geen aparte ContentAgent-logbron
                en Windows Event Log wordt niet opgehaald.
            </p>
            <p>
                <strong>Not found / missing</strong> betekent dat een verwacht logbestand niet is
                gevonden. Dat is niet automatisch een storing: een onderdeel of opdracht kan nog
                niet zijn gebruikt. Leesfouten worden apart vermeld. Bij <strong>limited</strong>
                is alleen een deel opgenomen, bijvoorbeeld het laatste deel van een groot bestand;
                de volledige historie is dan niet beschikbaar in de snapshot.
            </p>
            <h3>Fouten en waarschuwingen lezen</h3>
            <p>
                Het dashboard markeert vermoedelijke fouten en waarschuwingen op basis van de tekst,
                zoals ERROR, WARNING of een foutcode. Dit is een hulpmiddel, geen definitieve diagnose:
                lees ook de omliggende regels en kijk of de melding past bij het probleem en tijdstip.
                Geen gemarkeerde fouten is geen garantie dat alles goed werkt.
            </p>
            <p>
                Standaard tellen meldingen uit de laatste zeven dagen <strong>vanaf het moment van
                verzamelen</strong> mee. Je kunt ook Last 24 hours of Entire log kiezen.
                Oudere meldingen blijven gedimd zichtbaar in de volledige weergave.
                Met het foutenfilter en de navigatie tussen meldingen kun je gerichter zoeken.
            </p>
            <h3>Momentopname en privacy</h3>
            <p>
                Logs worden alleen op aanvraag opgehaald, niet continu of live. Het ophalen
                verplaatst of verwijdert geen lokale logbestanden en repareert zelf geen problemen.
                De opgeslagen snapshots zijn 30 dagen beschikbaar.
            </p>
            <p>
                Veelvoorkomende wachtwoorden en tokens worden voor upload gefilterd, maar logs
                kunnen nog steeds klantgegevens of andere gevoelige informatie bevatten.
                Controleer een download voordat je die deelt en deel alleen wat nodig is via
                de daarvoor afgesproken kanalen.
            </p>
        </section>

        <section className="documentation-section" aria-labelledby="online-heading">
            <h2 id="online-heading">5. Online-status: staat de pc nu aan?</h2>
            <p>
                De ContentAgent op de klant-pc vraagt <strong>elke 60 seconden</strong> aan de portal
                of er een opdracht klaarstaat. Elke vraag geldt als teken van leven. Heeft de agent zich
                in de <strong>laatste 5 minuten</strong> gemeld, dan staat het apparaat op
                <strong> Online</strong>. Wordt de pc uitgezet of valt internet weg, dan staat hij
                binnen ongeveer 5 minuten op <strong>Offline</strong>.
            </p>
            <ul>
                <li><strong>Last seen:</strong> het laatste moment dat de pc iets van zich liet horen, via de agent of via SystemReporter bij het opstarten.</li>
                <li><strong>Agent:</strong> de versie van de ContentAgent. Bij agent 1.0.2 staat hier een streepje; de online-status werkt dan wel gewoon.</li>
                <li>Een pc zonder werkende ContentAgent staat altijd op Offline, ook als hij aan staat. Kijk dan naar Last seen.</li>
                <li>De status is een momentopname bij het laden van de pagina. Klik op Refresh voor de actuele stand.</li>
            </ul>
            <p>
                Dit belast de klant-pc niet merkbaar: het is één klein internetverzoek per minuut, zonder
                invloed op realtime audio in Hauptwerk of Sweelinq. Is de portal niet bereikbaar, dan
                schrijft de agent (vanaf 1.0.3) één waarschuwing in het Windows-logboek en niet elke minuut opnieuw.
            </p>
        </section>

        <section className="documentation-section" aria-labelledby="workflow-heading">
            <h2 id="workflow-heading">Praktisch: een nieuwe optie gebruiken</h2>
            <ol>
                <li>Controleer of je het juiste klantapparaat hebt geopend en lees de release notes bij de update.</li>
                <li>Klik op Install Latest Update en controleer of de installatie succesvol is afgerond.</li>
                <li>Open de configurator op de klant-pc, kies de gewenste optie en klik op Update.</li>
                <li>Controleer op de klant-pc of de gewenste inrichting goed werkt.</li>
                <li>Bekijk na een volgende rapportage in de portal de nieuwe gegevens en controleer de rapportdatum.</li>
            </ol>
            <p>
                <strong>Let op de versies:</strong> de installerversie en de SystemReporter-versie horen
                bij verschillende onderdelen en hoeven niet gelijk te zijn. Een recente
                SystemReporter-versie betekent niet dat ook het nieuwste installer-updatepakket is toegepast.
            </p>
            <p>
                Zie je een actieknop niet of is die uitgeschakeld? Beheeracties zijn afhankelijk
                van je rechten en van de softwareversies op het apparaat.
            </p>
        </section>
    </main>
);

export default Documentation;
